import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query'
import { httpBatchLink } from '@trpc/client'
import { createTRPCReact, getMutationKey, getQueryKey } from '@trpc/react-query'
import type { inferRouterInputs, inferRouterOutputs } from '@trpc/server'
import { makeRefreshFetch } from '@wasp/refresh-fetch'
import type { FetchFn } from '@wasp/refresh-fetch'
import superjson, { deserialize as superjsonDeserialize } from 'superjson'

// TYPE ONLY, and it has to stay that way: `@wasp/api` is the backend, and a
// value import would drag fastify, pg and bcrypt into the bundle. It is a
// devDependency of this workspace for exactly this line.
import type { AppRouter } from '@wasp/api'

export type { AppRouter } from '@wasp/api'
export type RouterOutputs = inferRouterOutputs<AppRouter>
export type RouterInputs = inferRouterInputs<AppRouter>

/**
 * How often a screen re-reads the mirror, as `refetchInterval`. The mirror runs
 * up to two minutes behind and MARS keeps syncing whether anybody is looking or
 * not, so a plant screen that only reads on mount shows a state that ages
 * without ever being corrected — and the freshness badge would be measuring an
 * answer nothing renews.
 */
export const MIRROR_REFETCH_MS = 60_000

/**
 * The singleton tRPC React instance. Wrap the app with:
 *
 *   <trpc.Provider client={trpcClient} queryClient={queryClient}>
 *     <QueryClientProvider client={queryClient}>…</QueryClientProvider>
 *   </trpc.Provider>
 */
export const trpc = createTRPCReact<AppRouter>()

export type CreateTrpcClientOpts = {
  /**
   * Base URL of the tRPC endpoint. Defaults to `VITE_API_URL + '/trpc'` when the
   * variable is set at build time, and to the relative `/trpc` otherwise — which
   * in dev is the Vite proxy to :8080, same-origin, which is what makes the
   * httpOnly cookies work without CORS.
   */
  url?: string
  /**
   * The fetch to go through. Defaults to the global one; the test harness passes
   * its own stub so a spec never has to patch `globalThis.fetch` and remember to
   * put it back. Both the refresh POST and the wrapped request use it, so a stub
   * sees the whole conversation.
   */
  fetch?: FetchFn
}

/** The envelope superjson puts a value in on the wire — its own input type. */
type SuperjsonEnvelope = Parameters<typeof superjsonDeserialize>[0]

/**
 * Reads the payload out of a tRPC success body, letting superjson undo its own
 * wire format rather than indexing into `.json` by hand. The nesting is
 * superjson's business, not ours: reading the un-nested shape returns undefined
 * forever, and in retail that failed silently — every reload bounced to /login
 * because the refresh looked unsuccessful.
 */
const readSuccessPayload = (body: unknown): Record<string, unknown> | null => {
  const envelope = (body as { result?: { data?: unknown } } | null | undefined)?.result?.data
  if (envelope === null || typeof envelope !== 'object') {
    return null
  }
  try {
    const value = superjsonDeserialize(envelope as SuperjsonEnvelope)
    return value !== null && typeof value === 'object' ? (value as Record<string, unknown>) : null
  } catch {
    // A body that is not a superjson envelope is simply not a successful refresh.
    return null
  }
}

/**
 * Confirms a successful `auth.refresh` from its raw JSON body. The SPA never
 * reads the token value — the new pair rides the httpOnly Set-Cookie headers —
 * but a valid body is the signal that the refresh actually issued them, so
 * `makeRefreshFetch` should retry. Its own function so the wire shape is pinned
 * by a spec.
 */
export const isRefreshResponseOk = (body: unknown): boolean => {
  const accessToken = readSuccessPayload(body)?.accessToken
  return typeof accessToken === 'string' && accessToken.length > 0
}

/**
 * The two calls whose own 401 is the ANSWER, not a symptom of an expired
 * session — `makeRefreshFetch` hands their 401 straight back instead of
 * refreshing:
 *
 *   - `auth.refresh`, so a refresh issued through this client cannot recurse
 *     (dormant today: the refresh posts with a bare fetch);
 *   - `auth.login` (and, by the same prefix, `auth.loginWithGoogle`), which
 *     fires on every failed sign-in. Refreshing there spends a round-trip only
 *     to fail with no cookie to refresh, and its error REPLACES the login's —
 *     so the form would tell an operator their session expired when it never
 *     started.
 */
export const isSelfAnsweringAuthUrl = (url: Request | string | URL): boolean => {
  const str = typeof url === 'string' ? url : url instanceof URL ? url.href : String(url)
  return str.includes('auth.refresh') || str.includes('auth.login')
}

const resolveBaseUrl = (opts: CreateTrpcClientOpts): string => {
  if (opts.url !== undefined) {
    return opts.url
  }
  // Read through a narrowed shape: `ImportMetaEnv` publishes an `any` index
  // signature, and taking the value straight off it would type the base URL
  // `any` — the one place where the string that every request is built from
  // would stop being checked.
  const env: Record<string, string | undefined> = import.meta.env
  const apiUrl = env.VITE_API_URL
  return apiUrl !== undefined && apiUrl !== '' ? `${apiUrl}/trpc` : '/trpc'
}

/**
 * The tRPC client the whole SPA talks through:
 *
 *   - `httpBatchLink` with `credentials: 'include'` — the session is an httpOnly
 *     cookie pair, there is no Authorization header and no token in JS;
 *   - transparent refresh on 401 via `makeRefreshFetch`;
 *   - superjson, so the mirror's `Date`s arrive as `Date`s. Freshness is a pair
 *     of dates, and a front that parses strings back into dates gets it wrong
 *     once and then shows the wrong lag on every screen.
 */
export const createTrpcClient = (opts: CreateTrpcClientOpts = {}) => {
  const baseUrl = resolveBaseUrl(opts)
  const baseFetch: FetchFn = opts.fetch ?? ((url, init) => fetch(url, init))

  // A plain POST, deliberately OUTSIDE the refresh wrapper, so a failing refresh
  // cannot recurse into itself. Cookie mode: the server reads the refresh token
  // from its httpOnly cookie, so the input is empty — but the router runs the
  // superjson transformer, and an empty input has to travel as `{ json: {} }`. A
  // bare `{}` deserialises to `undefined`, zod rejects it with a 400, and in
  // retail that turned every reload into a bounce to /login.
  const refreshFn = async (): Promise<void> => {
    const res = await baseFetch(`${baseUrl}/auth.refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ json: {} }),
    })
    if (!res.ok || !isRefreshResponseOk(await res.json())) {
      throw new Error('UNAUTHORIZED')
    }
  }

  const refreshingFetch = makeRefreshFetch(
    (url, init) => baseFetch(url, { ...init, credentials: 'include' }),
    refreshFn,
    { skipRefreshFor: isSelfAnsweringAuthUrl },
  )

  return trpc.createClient({
    links: [
      httpBatchLink({
        url: baseUrl,
        fetch: refreshingFetch,
        transformer: superjson,
      }),
    ],
  })
}

/**
 * True when an error means "this session is over": either a tRPC UNAUTHORIZED,
 * or the bare `Error('UNAUTHORIZED')` that `makeRefreshFetch` throws when the
 * silent refresh itself failed (that one carries no tRPC `data` shape).
 */
export const isUnauthorizedError = (error: unknown): boolean => {
  if (error === null || error === undefined || typeof error !== 'object') {
    return false
  }
  const { data, message } = error as { data?: { code?: unknown }; message?: unknown }
  if (data?.code === 'UNAUTHORIZED') {
    return true
  }
  return typeof message === 'string' && message.includes('UNAUTHORIZED')
}

// `auth.me` is the query the route guards read, `auth.login` the mutation whose
// own 401 is the answer. `getQueryKey` / `getMutationKey` rather than
// hand-written arrays keeps both tied to the router: rename a procedure and this
// stops compiling instead of silently matching nothing.
const sessionQueryKey = getQueryKey(trpc.auth.me)
const loginMutationKey = getMutationKey(trpc.auth.login)
const googleLoginMutationKey = getMutationKey(trpc.auth.loginWithGoogle)

/**
 * Whether a failing query or mutation is the one `reference` names. Both keys
 * carry the procedure path as their first element, so one comparison serves the
 * two caches.
 */
const isProcedure = (key: ReadonlyArray<unknown> | undefined, reference: ReadonlyArray<unknown>): boolean => {
  const [path] = key ?? []
  const [referencePath] = reference
  return Array.isArray(path) && Array.isArray(referencePath) && path.join('.') === referencePath.join('.')
}

/**
 * The QueryClient, shared by `trpc.Provider` and `QueryClientProvider`.
 *
 * Expired sessions are handled centrally here. A session can die between two
 * renders — the refresh token's absolute cap passes, a manager deactivates the
 * person — and `auth.me` is cached with a staleTime, so nothing would notice:
 * the shell would keep the stale user while every other query showed an error.
 * Instead, any query or mutation that comes back UNAUTHORIZED invalidates
 * `auth.me`; its refetch 401s in turn and `RequireAuth` lands on /login.
 */
export const createQueryClient = (): QueryClient => {
  const invalidateSessionOnExpiry = (error: unknown, key: ReadonlyArray<unknown> | undefined): void => {
    if (!isUnauthorizedError(error)) {
      return
    }
    // Skip `auth.me`'s own failure: it is already the signal the guard reads,
    // and invalidating it here would refetch → fail → invalidate, forever.
    if (isProcedure(key, sessionQueryKey)) {
      return
    }
    // And skip the two sign-ins: a wrong password 401s on every failed sign-in, and
    // treating it as an expired session spends an `auth.me` + `auth.refresh`
    // pair against the rate-limited /trpc to learn there is no session to
    // refresh. Same reasoning as `isSelfAnsweringAuthUrl` above.
    if (isProcedure(key, loginMutationKey) || isProcedure(key, googleLoginMutationKey)) {
      return
    }
    void queryClient.invalidateQueries({ queryKey: sessionQueryKey })
  }

  const queryClient: QueryClient = new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => invalidateSessionOnExpiry(error, query.queryKey),
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) =>
        invalidateSessionOnExpiry(error, mutation.options.mutationKey),
    }),
    defaultOptions: {
      queries: {
        // 30 s — enough to stop a screen refetching everything on every mount,
        // short enough that mirror data on a plant screen stays honest. The
        // freshness badge is what says how old the DATA is; this is only cache.
        staleTime: 30_000,
        retry: false,
      },
    },
  })

  return queryClient
}
